/**
 * GitHub World State Service
 * Creates real git commits to github.com/diannt/lucid-cyber-world-state
 * Makes the collective narrative publicly visible
 */

import { Octokit } from '@octokit/rest';
import { supabaseClient } from './supabaseClient';

const GITHUB_OWNER = 'diannt';
const GITHUB_REPO = 'lucid-cyber-world-state';

interface CommitData {
  playerId: string;
  playerName: string;
  choice: string;
  karma: number;
  location: string;
  consequences: string;
  cartridgeId: string;
  cartridgeName: string;
  instanceId?: string;
  isSolo?: boolean;
}

class GitHubWorldStateService {
  private octokit: Octokit | null = null;
  private commitQueue: CommitData[] = [];
  private isProcessing = false;

  /**
   * Initialize GitHub client using server-side token
   * Note: GitHub token should be in environment variable
   */
  async initialize() {
    // For server-side only (Vercel functions)
    if (typeof window === 'undefined') {
      const token = process.env.GITHUB_TOKEN || process.env.GITHUB_PAT;
      if (token) {
        this.octokit = new Octokit({ auth: token });
        console.log('[GitHubWorld] Initialized with GitHub token');
      } else {
        console.warn('[GitHubWorld] No GitHub token found, commits will queue for server processing');
      }
    }
  }

  /**
   * Create a player choice commit to GitHub
   * This makes the choice publicly visible at github.com/diannt/lucid-cyber-world-state
   */
  async createPlayerChoiceCommit(data: CommitData): Promise<string | null> {
    try {
      // Queue the commit for background processing
      await this.queueCommit(data);

      if (data.playerId) {
        // Return immediately (don't block game)
        console.log(`[GitHubWorld] Queued commit for ${data.playerName ?? data.playerId}`);
      }

      // Process queue if not already processing
      if (!this.isProcessing) {
        this.processQueue();
      }

      return `queued_${Date.now()}`;
    } catch (error) {
      console.error('[GitHubWorld] Error queuing commit:', error);
      return null;
    }
  }

  /**
   * Queue commit to database for server-side processing
   */
  private async queueCommit(data: CommitData): Promise<void> {
    try {
      // Don't queue solo instances - they stay private
      if (data.isSolo) {
        console.log('[GitHubWorld] Solo instance - not queuing for GitHub');
        return;
      }

      if (!data.playerId) {
        console.warn('[GitHubWorld] Skipping GitHub queue due to missing player id');
        return;
      }

      const { error } = await supabaseClient
        .from('github_sync_queue')
        .insert({
          player_id: data.playerId,
          player_name: data.playerName ?? 'Unknown Dreamer',
          cartridge_id: data.cartridgeId,
          cartridge_name: data.cartridgeName,
          instance_id: data.instanceId || 'default',
          commit_data: {
            choice: data.choice,
            karma: data.karma,
            location: data.location,
            consequences: data.consequences
          },
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (error) {
        console.error('[GitHubWorld] Failed to queue commit:', error);
      }
    } catch (error) {
      console.error('[GitHubWorld] Queue error:', error);
    }
  }

  /**
   * Process queued commits (background worker)
   */
  private async processQueue() {
    if (this.isProcessing) return;

    this.isProcessing = true;

    try {
      // Get pending commits
      const { data: pending, error } = await supabaseClient
        .from('github_sync_queue')
        .select('*')
        .eq('status', 'pending')
        .limit(10);

      if (error || !pending || pending.length === 0) {
        this.isProcessing = false;
        return;
      }

      console.log(`[GitHubWorld] Processing ${pending.length} queued commits`);

      for (const item of pending) {
        try {
          const sha = await this.commitToGitHub(item);

          if (sha) {
            // Mark as synced
            await supabaseClient
              .from('github_sync_queue')
              .update({
                status: 'synced',
                github_commit_sha: sha,
                synced_at: new Date().toISOString()
              })
              .eq('id', item.id);

            console.log(`[GitHubWorld] Synced commit: ${sha}`);
          } else {
            // Mark as failed
            await supabaseClient
              .from('github_sync_queue')
              .update({ status: 'failed', retry_count: (item.retry_count || 0) + 1 })
              .eq('id', item.id);
          }
        } catch (err) {
          console.error('[GitHubWorld] Commit processing error:', err);
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Actually commit to GitHub (server-side only)
   */
  private async commitToGitHub(queueItem: any): Promise<string | null> {
    if (!this.octokit) {
      console.warn('[GitHubWorld] No GitHub client, cannot commit');
      return null;
    }

    try {
      const { player_name, cartridge_name, commit_data } = queueItem;
      const { choice, karma, location, consequences } = commit_data;

      // Convert cartridge name to slug
      const cartridgeSlug = (cartridge_name || 'default').toLowerCase().replace(/[^a-z0-9]+/g, '-');

      // Determine branch based on cartridge and karma
      const branch = this.getBranchForCartridgeAndKarma(cartridgeSlug, karma);

      // Format commit message
      const message = `[${player_name}] ${choice} (Karma: ${karma >= 0 ? '+' : ''}${karma})

Location: ${location}
Consequences: ${consequences}
Cartridge: ${cartridge_name}
Timestamp: ${new Date().toISOString()}`;

      // Create file content (JSONL format for easy append)
      const filename = `${branch}/choices.jsonl`;
      const content = JSON.stringify({
        player: player_name,
        choice,
        karma,
        location,
        consequences,
        cartridge: cartridge_name,
        timestamp: new Date().toISOString()
      }) + '\n';

      // Get current file content
      let currentContent = '';
      try {
        const { data: fileData } = await this.octokit.repos.getContent({
          owner: GITHUB_OWNER,
          repo: GITHUB_REPO,
          path: filename,
          ref: branch
        });

        if ('content' in fileData) {
          currentContent = Buffer.from(fileData.content, 'base64').toString('utf-8');
        }
      } catch (err: any) {
        if (err.status !== 404) throw err;
        // File doesn't exist yet, that's fine
      }

      // Append new choice
      const newContent = currentContent + content;

      // Create commit
      const { data: commit } = await this.octokit.repos.createOrUpdateFileContents({
        owner: GITHUB_OWNER,
        repo: GITHUB_REPO,
        path: filename,
        message,
        content: Buffer.from(newContent).toString('base64'),
        branch,
        sha: currentContent ? (await this.octokit.repos.getContent({
          owner: GITHUB_OWNER,
          repo: GITHUB_REPO,
          path: filename,
          ref: branch
        })).data.sha : undefined
      });

      return commit.commit.sha;
    } catch (error) {
      console.error('[GitHubWorld] GitHub commit error:', error);
      return null;
    }
  }

  /**
   * Determine which branch to commit to based on cartridge and karma
   */
  private getBranchForCartridgeAndKarma(cartridgeSlug: string, karma: number): string {
    // Convert cartridge name to slug format
    const slug = cartridgeSlug.toLowerCase().replace(/[^a-z0-9]+/g, '-');

    // Determine karma path
    let karmaPath = 'balanced';
    if (karma >= 50) karmaPath = 'light';
    else if (karma <= -50) karmaPath = 'shadow';
    else if (karma < -10) karmaPath = 'shadow';
    else if (karma > 10) karmaPath = 'light';

    return `cartridges/${slug}/${karmaPath}`;
  }

  /**
   * Legacy method for backward compatibility
   */
  private getBranchForKarma(karma: number): string {
    if (karma >= 50) return 'cartridges/default/light';
    if (karma <= -50) return 'cartridges/default/shadow';
    return 'cartridges/default/balanced';
  }

  /**
   * Get player's current GitHub branch
   */
  async getPlayerBranch(playerId: string): Promise<string> {
    // For now, use cohort branches
    // Later: individual player branches can be created
    const { data } = await supabaseClient
      .from('player_choice_commits')
      .select('karma_delta')
      .eq('player_id', playerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    const karma = data?.karma_delta || 0;
    return this.getBranchForKarma(karma);
  }

  /**
   * View commit on GitHub
   */
  getCommitUrl(sha: string): string {
    return `https://github.com/${GITHUB_OWNER}/${GITHUB_REPO}/commit/${sha}`;
  }

  /**
   * View player's branch on GitHub
   */
  getBranchUrl(branch: string): string {
    return `https://github.com/${GITHUB_OWNER}/${GITHUB_REPO}/tree/${branch}`;
  }
}

export const githubWorldStateService = new GitHubWorldStateService();
